import React, { useState, useMemo, useEffect } from 'react';
import { Committee, Subcommittee, Politician } from '../api/models/models';
import { ChevronDownIcon, ChevronRightIcon, UserGroupIcon, BuildingOfficeIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { congressionalApi, setTokenProvider } from '../services/congressionalApiService';
import { useAppSelector } from '../store/hooks';

interface SubcommitteeOverviewComponentProps {
    onSubcommitteeSelect?: (subcommittee: Subcommittee) => void;
    onPoliticianSelect?: (politician: Politician) => void;
    onCommitteeSelect?: (committee: Committee) => void;
    onNavigateToCommittees?: () => void;
    selectedCommitteeId?: string | null; // Filter subcommittees by parent committee
    onClearCommitteeFilter?: () => void; // Clear the committee filter
}

const SubcommitteeOverviewComponent: React.FC<SubcommitteeOverviewComponentProps> = ({
    onSubcommitteeSelect,
    onPoliticianSelect,
    onCommitteeSelect,
    onNavigateToCommittees,
    selectedCommitteeId,
    onClearCommitteeFilter
}) => {
    const [committees, setCommittees] = useState<Committee[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedSubcommittees, setExpandedSubcommittees] = useState<Set<string>>(new Set());
    const [sortBy, setSortBy] = useState<'name' | 'members' | 'committee'>('name');
    
    const credentials = useAppSelector((state) => state.user.credentials);

    // Set up token provider
    useEffect(() => {
        setTokenProvider({
            getAccessToken: () => credentials?.accessToken || null
        });
    }, [credentials]);

    // Fetch committees data to get subcommittees
    useEffect(() => {
        const fetchCommittees = async () => {
            if (!credentials?.accessToken) {
                setError('No access token available');
                setLoading(false);
                return;
            }
            
            try {
                setLoading(true);
                const committeesData = await congressionalApi.getCommittees();
                setCommittees(committeesData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch committees');
                console.error('Error fetching committees:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchCommittees();
    }, [credentials?.accessToken]);

    // Extract all subcommittees with parent committee info
    const allSubcommittees = useMemo(() => {
        const subcommittees: (Subcommittee & { parentCommittee: Committee })[] = [];
        committees.forEach(committee => {
            committee.subcommittees?.forEach(subcommittee => {
                subcommittees.push({
                    ...subcommittee,
                    parentCommittee: committee
                });
            });
        });
        return subcommittees;
    }, [committees]);

    // Filter and sort subcommittees
    const filteredAndSortedSubcommittees = useMemo(() => {
        let filtered = allSubcommittees.filter(subcommittee => {
            // Text search filter - split search term into words and match each word separately
            const matchesSearch = (() => {
                if (!searchTerm.trim()) return true;
                
                const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
                
                // Build searchable text from subcommittee, parent committee, and member names
                let searchableText = `${subcommittee.name} ${subcommittee.parentCommittee.name}`;
                if (subcommittee.members) {
                    const memberNames = subcommittee.members
                        .map(member => member.politician?.name || '')
                        .join(' ');
                    searchableText += ` ${memberNames}`;
                }
                searchableText = searchableText.toLowerCase();
                
                // All search words must be found somewhere in the searchable text
                return searchWords.every(word => searchableText.includes(word));
            })();

            // Parent committee filter
            const matchesCommittee = !selectedCommitteeId || subcommittee.parentCommittee.id === selectedCommitteeId;

            return matchesSearch && matchesCommittee;
        });

        // Sort subcommittees
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'members':
                    return (b.members?.length || 0) - (a.members?.length || 0);
                case 'committee':
                    return a.parentCommittee.name.localeCompare(b.parentCommittee.name);
                default:
                    return 0;
            }
        });

        return filtered;
    }, [allSubcommittees, searchTerm, sortBy, selectedCommitteeId]);

    const toggleSubcommitteeExpansion = (subcommitteeId: string) => {
        const newExpanded = new Set(expandedSubcommittees);
        if (newExpanded.has(subcommitteeId)) {
            newExpanded.delete(subcommitteeId);
        } else {
            newExpanded.add(subcommitteeId);
        }
        setExpandedSubcommittees(newExpanded);
    };

    const getPartyColor = (party: string) => {
        if (party.toLowerCase().includes('democrat')) return 'text-blue-600';
        if (party.toLowerCase().includes('republican')) return 'text-red-600';
        return 'text-gray-600';
    };

    const getPartyBadgeColor = (party: string) => {
        if (party.toLowerCase().includes('democrat')) return 'bg-blue-100 text-blue-800';
        if (party.toLowerCase().includes('republican')) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const renderPolitician = (politician: Politician) => (
        <div
            key={politician.id}
            className="flex items-center justify-between p-3 rounded-lg border border-neutral-200 cursor-pointer hover:bg-neutral-50 hover:border-primary-300 transition-colors"
            onClick={() => onPoliticianSelect?.(politician)}
        >
            <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-sm font-semibold text-white">
                        {politician.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                </div>
                <div>
                    <p className="font-semibold text-neutral-900">{politician.name}</p>
                    <p className="text-sm text-neutral-600">{politician.state} - {politician.district}</p>
                </div>
            </div>
            <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPartyBadgeColor(politician.party)}`}>
                    {politician.party.charAt(0)}
                </span>
            </div>
        </div>
    );

    const renderSubcommittee = (subcommittee: Subcommittee & { parentCommittee: Committee }) => {
        const isExpanded = expandedSubcommittees.has(subcommittee.id);
        const hasMembers = subcommittee.members && subcommittee.members.length > 0;
        const memberCount = subcommittee.members?.length || 0;

        return (
            <div key={subcommittee.id} className="border border-neutral-200 rounded-lg mb-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-50 bg-white"
                    onClick={() => {
                        if (hasMembers) toggleSubcommitteeExpansion(subcommittee.id);
                        onSubcommitteeSelect?.(subcommittee);
                    }}
                >
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            {hasMembers ? (
                                isExpanded ? (
                                    <ChevronDownIcon className="w-5 h-5 text-neutral-400" />
                                ) : (
                                    <ChevronRightIcon className="w-5 h-5 text-neutral-400" />
                                )
                            ) : (
                                <div className="w-5 h-5" />
                            )}
                            <UserGroupIcon className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-neutral-900 text-lg">{subcommittee.name}</h3>
                            <div
                                className="flex items-center space-x-2 mt-1 cursor-pointer hover:text-primary-600 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCommitteeSelect?.(subcommittee.parentCommittee);
                                }}
                            >
                                <BuildingOfficeIcon className="w-4 h-4 text-neutral-400" />
                                <span className="text-sm text-neutral-600 hover:text-primary-600 transition-colors">
                                    {subcommittee.parentCommittee.name}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        <div className="text-center min-w-[60px]">
                            <div className="text-2xl font-bold text-primary-600">{memberCount}</div>
                            <div className="text-xs text-neutral-500">members</div>
                        </div>

                        <div className="flex flex-col space-y-2 min-w-[120px]">
                            {subcommittee.link ? (
                                <a
                                    href={subcommittee.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    View Details →
                                </a>
                            ) : (
                                <div className="h-5" />
                            )}
                        </div>
                    </div>
                </div>

                {isExpanded && hasMembers && (
                    <div className="border-t border-neutral-200 bg-neutral-50 p-4">
                        <h4 className="font-semibold text-neutral-700 mb-3 flex items-center">
                            <UserGroupIcon className="w-4 h-4 mr-2" />
                            Subcommittee Members ({memberCount})
                        </h4>
                        <div className="grid gap-3">
                            {subcommittee.members?.map(member =>
                                member.politician && renderPolitician(member.politician)
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="mt-4 text-neutral-600">Loading subcommittees...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="text-red-600 text-xl mb-2">⚠️</div>
                        <p className="text-red-600 font-semibold">Error loading subcommittees</p>
                        <p className="text-neutral-600 text-sm mt-2">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalMembers = allSubcommittees.reduce((sum, sub) => sum + (sub.members?.length || 0), 0);

    return (
        <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-2xl font-semibold text-primary-900">
                            Congressional Subcommittees ({filteredAndSortedSubcommittees.length})
                        </h2>
                        {selectedCommitteeId && (() => {
                            const selectedCommittee = committees.find(c => c.id === selectedCommitteeId);
                            return selectedCommittee ? (
                                <div className="flex items-center gap-2 mt-1">
                                    <p className="text-sm text-secondary-600">
                                        Showing subcommittees for: <span className="font-medium">{selectedCommittee.name}</span>
                                    </p>
                                    <button
                                        onClick={() => {
                                            if (onClearCommitteeFilter) {
                                                onClearCommitteeFilter();
                                            }
                                        }}
                                        className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded hover:bg-neutral-200 transition-colors"
                                    >
                                        ✕ Clear Filter
                                    </button>
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mb-4 mt-4">
                    {/* Search */}
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Search subcommittees, committees, or members..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>

                    {/* Sort */}
                    <div className="sm:w-48">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="name">Sort by Name</option>
                            <option value="members">Sort by Members</option>
                            <option value="committee">Sort by Committee</option>
                        </select>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg border border-primary-200">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <p className="text-2xl font-bold text-primary-600">{allSubcommittees.length}</p>
                            <p className="text-sm text-neutral-600">Total Subcommittees</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <p className="text-2xl font-bold text-primary-600">{committees.length}</p>
                            <p className="text-sm text-neutral-600">Parent Committees</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <p className="text-2xl font-bold text-primary-600">{totalMembers}</p>
                            <p className="text-sm text-neutral-600">Total Members</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <p className="text-2xl font-bold text-primary-600">
                                {Math.round(totalMembers / Math.max(allSubcommittees.length, 1))}
                            </p>
                            <p className="text-sm text-neutral-600">Avg Members</p>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-6 flex flex-wrap gap-2">
                    <button
                        onClick={() => setExpandedSubcommittees(new Set(allSubcommittees.map(s => s.id)))}
                        className="px-4 py-2 text-sm bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200 transition-colors"
                    >
                        Expand All
                    </button>
                    <button
                        onClick={() => setExpandedSubcommittees(new Set())}
                        className="px-4 py-2 text-sm bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200 transition-colors"
                    >
                        Collapse All
                    </button>
                </div>
            </div>

            {/* Subcommittees List */}
            <div className="space-y-4">
                {filteredAndSortedSubcommittees.map(subcommittee => renderSubcommittee(subcommittee))}
            </div>

            {filteredAndSortedSubcommittees.length === 0 && !loading && (
                <div className="text-center py-12 text-neutral-500">
                    <UserGroupIcon className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
                    <h3 className="font-semibold text-neutral-600 mb-2">No subcommittees found</h3>
                    <p>No subcommittees match your search criteria.</p>
                </div>
            )}
        </div>
    );
};

export default SubcommitteeOverviewComponent;
